/**
 * What an alert found, in detail: the fields its check recorded, in words.
 *
 * An alert row said "Contify removed 58 pages under /case-study" and nothing
 * more, while the alert carried the pages themselves. Clicking the row shows
 * them. Pure, so CRA's jest can test it.
 */
import type { WatchEvent } from '../../types';

export interface DetailLine {
  label: string;
  /** Plain text. */
  value?: string;
  /** Pages or sites, each a link when it looks like one. */
  links?: { text: string; href: string | null }[];
}

/** Fields already said in the headline, or only for the system. */
const SKIP = new Set(['name', 'z', 'subject', 'compared_with']);

/** "Oct 19, 4:48 PM", in the reader's own zone. */
export function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface Compared {
  first_taken?: string | null;
  last_measured?: string | null;
  checks?: number;
  count_before?: number;
  count_after?: number;
  /** The check that took the earlier reading could not read all of it. */
  partial?: boolean;
}

/** A change measured against a partial reading, said, or null. Pure. */
export function partialWarning(event: Pick<WatchEvent, 'payload'>): string | null {
  const c = (event.payload ?? {}).compared_with as Compared | undefined;
  return c?.partial
    ? `⚠ Compared with a partial reading${c.last_measured ? ` (${when(c.last_measured)})` : ''}: likely not a real change.`
    : null;
}

/** The one line under an alert's headline: since when, and from what.
 *  "Since the check of Oct 19, 3:47 PM · pages on the site 89 → 249". Pure. */
export function sinceLine(event: Pick<WatchEvent, 'payload'>): string | null {
  const c = (event.payload ?? {}).compared_with as Compared | undefined;
  if (!c || !c.last_measured) return null;
  const pages =
    typeof c.count_before === 'number' && typeof c.count_after === 'number'
      ? ` · pages on the site ${c.count_before} → ${c.count_after}`
      : '';
  return `Since the check of ${when(c.last_measured)}${pages}`;
}

/** The reading a change was measured against, and when it was last checked. Pure. */
export function comparedLines(compared: Compared | undefined | null): DetailLine[] {
  if (!compared || !compared.last_measured) return [];
  const checks = compared.checks ?? 1;
  const since =
    compared.first_taken && compared.first_taken !== compared.last_measured
      ? `, the same since ${when(compared.first_taken)} (${checks} check${checks === 1 ? '' : 's'})`
      : '';
  const out: DetailLine[] = [
    { label: 'Compared with', value: `the reading last taken ${when(compared.last_measured)}${since}` },
  ];
  if (typeof compared.count_before === 'number') {
    out.push({
      label: 'Pages on the site',
      value:
        typeof compared.count_after === 'number'
          ? `${compared.count_before} → ${compared.count_after}`
          : `${compared.count_before} before`,
    });
  }
  return out;
}

const LABELS: Record<string, string> = {
  sample: 'Pages',
  added: 'New text',
  headings_added: 'New headings',
  headings_removed: 'Headings removed',
  where: 'Where',
  query: 'Search',
  detail: 'Detail',
  url: 'Page',
  plan: 'Plan',
  delta: 'Change',
  total: 'Out of',
  sources: 'Sources',
  competition: 'Competition',
  as_of: 'As of',
  changed: 'Text changed',
  title_changed: 'Title changed',
};

/** "contify.com/x" or "https://…": something to open. */
export function asHref(text: string): string | null {
  const t = text.trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(t)) return `https://${t}`;
  return null;
}

function words(key: string): string {
  return LABELS[key] ?? key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}

/** The alert's fields as lines to show. Pure. */
export function eventDetails(event: Pick<WatchEvent, 'kind' | 'payload'>): DetailLine[] {
  const p = event.payload ?? {};
  // WHAT IT CHANGED FROM FIRST: the earlier reading, and when it was measured.
  const out: DetailLine[] = comparedLines(p.compared_with as Compared | undefined);
  const rank = 'query' in p;
  // A position change reads as one line, "was → now".
  if ('was' in p || 'now' in p) {
    const pos = (v: unknown) => (v === null || v === undefined || v === '' ? 'not on page one' : `#${v}`);
    if (rank) out.push({ label: 'Position', value: `${pos(p.was)} → ${pos(p.now)}` });
    else out.push({ label: 'Was → now', value: `${p.was ?? '-'} → ${p.now ?? '-'}` });
  }
  if ('rate' in p && 'baseline' in p) {
    out.push({ label: 'Pages this period', value: `${p.rate} (usually about ${p.baseline})` });
  }
  if ('words_before' in p && 'words_after' in p) {
    out.push({ label: 'Words on the page', value: `${p.words_before} → ${p.words_after}` });
  }
  for (const [key, raw] of Object.entries(p)) {
    if (SKIP.has(key) || ['was', 'now', 'rate', 'baseline', 'words_before', 'words_after'].includes(key)) continue;
    if (raw === null || raw === undefined || raw === '') continue;
    if (key === 'count') {
      out.push({ label: rank ? 'Places moved' : 'How many', value: String(raw) });
      continue;
    }
    if (Array.isArray(raw)) {
      const items = raw.map(String).filter(Boolean);
      // The alert keeps a few examples, not every page: say which share.
      const of =
        key === 'sample' && typeof p.count === 'number' && p.count > items.length
          ? ` (${items.length} of ${p.count})`
          : '';
      if (items.length)
        out.push({ label: `${words(key)}${of}`, links: items.map(t => ({ text: t, href: asHref(t) })) });
      continue;
    }
    if (typeof raw === 'object') continue;
    const text = String(raw)
      .replace(/^\s*under\s+/, '')
      .trim();
    if (key === 'changed') {
      out.push({ label: words(key), value: `${text}%` });
      continue;
    }
    const href = asHref(text);
    out.push(href ? { label: words(key), links: [{ text, href }] } : { label: words(key), value: text });
  }
  return out;
}
